import React from 'react';
import { Form, InputGroup, Spinner, Row, Col } from 'react-bootstrap';
import { MapPin, Check, AlertCircle } from 'lucide-react';

import { suggestAddress, validateAddress } from '../../services/address';
import StateSelect from './StateSelect';

// Empty-shape factory keeps callers/consumers reading a consistent object even
// before the user picks anything.
export const EMPTY_ADDRESS = Object.freeze({
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  latitude: null,
  longitude: null,
});

const DEBOUNCE_MS = 300;

const formatSecondary = (a) => {
  const cityState = [a?.city, a?.state].filter(Boolean).join(', ');
  return [cityState, a?.zip].filter(Boolean).join(' ');
};

/**
 * AddressInput — typeahead-validated US address picker.
 *
 * Controlled component. `value` is the structured address object (see
 * EMPTY_ADDRESS); `onChange(next)` fires with the full new object on every
 * selection or line2 edit. Picking a suggestion populates line1/city/state/zip
 * /country/lat/lng; the user can still edit line2 (Apt/Suite) freely.
 *
 * Save-time rule (enforced by parent forms + the backend):
 * the address must be EMPTY or VERIFIED. Verified = all four required strings
 * (line1/city/state/zip) populated AND lat/lng non-null. Partial state is
 * rejected on save.
 *
 * Typing in the line1 input nulls lat/lng AND clears city/state/zip — a fresh
 * line1 invalidates any previously-picked suggestion until the user picks a
 * new one. This avoids stale city/state/zip sticking around after the user
 * backspaces an address they no longer want.
 */
const AddressInput = ({
  value,
  onChange,
  disabled = false,
  required = false,
  // When true, the unverified pill / warning UI renders alongside the input
  // when the address is partial. Parent forms still own the save gate — this
  // prop only controls the visual cue.
  requireVerified = false,
  label = 'Address',
  placeholder = 'Start typing a US street address...',
  idPrefix = 'address',
}) => {
  const v = value || EMPTY_ADDRESS;
  const [query, setQuery] = React.useState(v.line1 || '');
  const [suggestions, setSuggestions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [highlighted, setHighlighted] = React.useState(-1);
  const [error, setError] = React.useState('');
  // When true, render explicit city / state / zip inputs so the user can
  // finish the form even if /suggest is unreachable (rate-limited, network
  // down, no Smarty subscription, etc.). Flips on automatically when
  // runSearch errors; user can also leave it on after a successful pick.
  const [manualMode, setManualMode] = React.useState(false);
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const abortRef = React.useRef(null);

  // Re-sync the visible query when the parent resets value externally (e.g.
  // after form submit clears it).
  React.useEffect(() => {
    if ((value?.line1 || '') !== query && document.activeElement !== inputRef.current) {
      setQuery(value?.line1 || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.line1]);

  // Click-outside closes the suggestion list.
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setHighlighted(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const runSearch = React.useCallback(async (q) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');
    try {
      const out = await suggestAddress(q, { signal: controller.signal });
      setSuggestions(out);
      setOpen(out.length > 0);
      setHighlighted(out.length > 0 ? 0 : -1);
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      const status = e?.response?.status;
      // 429 is a rate-limit hit (per-IP cap reached) — friendlier wording so
      // the user knows it's not a permanent break. Any other failure (no
      // Smarty subscription, network, timeout) shows the generic message.
      // Both auto-expand the manual fields so the user can still complete
      // the form.
      setError(
        status === 429
          ? 'Lookup paused for a few minutes — enter your address manually below.'
          : 'Address lookup unavailable — enter your address manually below.',
      );
      setManualMode(true);
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (next) => {
    setQuery(next);
    // Typing in the search field invalidates the previously-picked
    // suggestion entirely. Null the geocode AND the city/state/zip until a
    // new suggestion is picked — otherwise stale fields hang around (the
    // "Edinburg, TX 78538" / "Colorado Springs, CO 80919" gotcha) and the
    // save gate stays partial-stuck after the user clears the input.
    onChange?.({
      ...v,
      line1: next,
      city: '',
      state: '',
      zip: '',
      latitude: null,
      longitude: null,
    });

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!next || next.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(next), DEBOUNCE_MS);
  };

  const handlePick = async (s) => {
    // Apply what we have from the suggestion immediately. Smarty's autocomplete
    // is coordinate-less, so we follow up with a validate() call to fetch
    // lat/lng — until that resolves, the form holds the address components
    // but lat/lng are null,
    // which means the "verified" pill is held off intentionally.
    const preserveLine2 = v.line2 || '';
    onChange?.({
      line1: s.line1 || '',
      line2: preserveLine2,
      city: s.city || '',
      state: s.state || '',
      zip: s.zip || '',
      country: s.country || 'US',
      latitude: s.latitude ?? null,
      longitude: s.longitude ?? null,
    });
    setQuery(s.line1 || '');
    setOpen(false);
    setHighlighted(-1);
    inputRef.current?.blur();

    if (s.latitude != null && s.longitude != null) return;

    // Smarty path — hydrate lat/lng + canonical fields via validate.
    setLoading(true);
    try {
      const result = await validateAddress({
        line1: s.line1,
        line2: null,
        city: s.city,
        state: s.state,
        zip: s.zip,
        country: s.country || 'US',
      });
      if (result?.ok && result.suggestion) {
        const c = result.suggestion;
        onChange?.({
          line1: c.line1 || s.line1 || '',
          line2: preserveLine2,
          city: c.city || s.city || '',
          state: c.state || s.state || '',
          zip: c.zip || s.zip || '',
          country: c.country || 'US',
          latitude: c.latitude ?? null,
          longitude: c.longitude ?? null,
        });
      }
    } catch (_) {
      // Leave the address in place but unverified — the parent form's
      // submit handler decides whether to block or warn.
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      if (highlighted >= 0 && highlighted < suggestions.length) {
        e.preventDefault();
        handlePick(suggestions[highlighted]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const geocoded = v.latitude != null && v.longitude != null;
  const unverifiedWarning =
    requireVerified &&
    !geocoded &&
    (query || '').trim().length >= 3 &&
    !loading;
  const showSecondaryDisplay = Boolean(v.city || v.state || v.zip);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {error && (
        <Form.Text className="text-muted d-flex align-items-center mb-2">
          <AlertCircle size={12} className="me-1" />
          {error}
        </Form.Text>
      )}
      <Form.Group controlId={`${idPrefix}-line1`} className="mb-2">
        <Form.Label>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </Form.Label>
        {/* Anchor wrapper: positions the suggestion dropdown directly under
            the InputGroup instead of the outer container (which would push
            it past line2 + the secondary display). */}
        <div style={{ position: 'relative' }}>
        {manualMode ? (
          <Form.Control
            type="text"
            name={`${idPrefix}-street`}
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            value={v.line1 || ''}
            placeholder="Street address"
            disabled={disabled}
            onChange={(e) =>
              onChange?.({
                ...v,
                line1: e.target.value,
                city: '',
                state: '',
                zip: '',
                latitude: null,
                longitude: null,
              })
            }
          />
        ) : (
        <InputGroup>
          <InputGroup.Text>
            <MapPin size={16} />
          </InputGroup.Text>
          <Form.Control
            ref={inputRef}
            type="text"
            // Chrome ignores autoComplete="off" on address-shaped fields, so
            // we use a non-standard value Chrome doesn't recognize, and a
            // name unrelated to "address" so its heuristics don't kick in.
            // data-* attributes help block 1Password / LastPass too.
            name={`${idPrefix}-q`}
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            value={query}
            placeholder={placeholder}
            disabled={disabled}
            spellCheck="false"
            onChange={(e) => handleQueryChange(e.target.value)}
            onFocus={() => {
              if (suggestions.length) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
          />
          {loading && (
            <InputGroup.Text>
              <Spinner animation="border" size="sm" />
            </InputGroup.Text>
          )}
          {!loading && geocoded && (
            <InputGroup.Text title="Address verified">
              <Check size={16} className="text-success" />
            </InputGroup.Text>
          )}
        </InputGroup>
        )}

        {open && suggestions.length > 0 && (
          <div
            role="listbox"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '100%',
              zIndex: 1050,
              marginTop: 4,
              borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.1)',
              background: 'var(--bs-body-bg, #fff)',
              boxShadow: '0 10px 28px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            }}
          >
            {suggestions.map((s, i) => (
              <button
                // Smarty sometimes returns multiple suggestions with the same
                // feature id (e.g. ZIP+4 variants of one address) — the index
                // suffix guarantees a unique React key.
                key={`${s.id || s.line1 || 'addr'}-${i}`}
                type="button"
                role="option"
                aria-selected={i === highlighted}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handlePick(s);
                }}
                onMouseEnter={() => setHighlighted(i)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 'none',
                  background:
                    i === highlighted ? 'rgba(13,110,253,0.08)' : 'transparent',
                  cursor: 'pointer',
                  borderBottom:
                    i < suggestions.length - 1
                      ? '1px solid rgba(0,0,0,0.05)'
                      : 'none',
                }}
              >
                <div style={{ fontWeight: 500, fontSize: 14 }}>
                  {s.line1 || s.label}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {formatSecondary(s)}
                </div>
              </button>
            ))}
          </div>
        )}
        </div>
        {unverifiedWarning && (
          <Form.Text className="d-flex align-items-center mt-1 text-danger">
            <AlertCircle size={12} className="me-1" />
            Please pick a suggestion to verify your address.
          </Form.Text>
        )}
      </Form.Group>

      <Form.Group controlId={`${idPrefix}-line2`} className="mb-2">
        <Form.Control
          type="text"
          name={`${idPrefix}-l2`}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          value={v.line2 || ''}
          placeholder="Apt, suite, unit (optional)"
          disabled={disabled}
          onChange={(e) => onChange?.({ ...v, line2: e.target.value })}
        />
      </Form.Group>

      {/* Manual fallback — auto-shown when /suggest fails so the user can
          finish the form. Stays editable after a successful pick too in
          case they need to correct anything. */}
      {manualMode && (
        <Row className="mb-2">
          <Col xs={12} sm={5} className="mb-2">
            <Form.Label className="small mb-1">City</Form.Label>
            <Form.Control
              type="text"
              name={`${idPrefix}-city`}
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              value={v.city || ''}
              disabled={disabled}
              onChange={(e) => onChange?.({ ...v, city: e.target.value })}
            />
          </Col>
          <Col xs={6} sm={3} className="mb-2">
            <Form.Label className="small mb-1">State</Form.Label>
            <StateSelect
              name={`${idPrefix}-state`}
              value={v.state || ''}
              onChange={(e) =>
                onChange?.({ ...v, state: e.target.value || '' })
              }
            />
          </Col>
          <Col xs={6} sm={4} className="mb-2">
            <Form.Label className="small mb-1">ZIP</Form.Label>
            <Form.Control
              type="text"
              name={`${idPrefix}-zip`}
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              value={v.zip || ''}
              maxLength={5}
              disabled={disabled}
              onChange={(e) =>
                onChange?.({
                  ...v,
                  zip: (e.target.value || '').replace(/\D/g, '').slice(0, 5),
                })
              }
            />
          </Col>
        </Row>
      )}

      {!manualMode && (showSecondaryDisplay || (query || '').trim().length >= 3) && (
        <div
          className="d-flex align-items-center gap-2 px-1 py-1 small text-muted"
          style={{ minHeight: 22 }}
        >
          <MapPin size={12} />
          <span>{formatSecondary(v) || (query || '').trim()}</span>
          {geocoded ? (
            <span
              className="ms-1 px-2 py-0 rounded-pill"
              style={{
                fontSize: 10,
                background: 'rgba(25,135,84,0.12)',
                color: '#198754',
              }}
            >
              verified
            </span>
          ) : (
            <span
              className="ms-1 px-2 py-0 rounded-pill"
              style={{
                fontSize: 10,
                background: 'rgba(245,158,11,0.15)',
                color: '#b45309',
              }}
              title="No matching address was selected — saved as-is. An admin can verify it later."
            >
              unverified
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressInput;
